import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  addWorkExperience, 
  updateWorkExperience, 
  deleteWorkExperience,
  addInternship,
  updateInternship,
  deleteInternship,
  addAchievement,
  updateAchievement,
  deleteAchievement 
} from '../../../store/slices/experienceSlice';
import Icon from '../../../components/AppIcon';
import ExperienceForm from './ExperienceForm';

const ExperienceAdmin = () => {
  const dispatch = useDispatch();
  const { workExperience, internshipsAndLearning, achievements } = useSelector(state => state.experience);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formType, setFormType] = useState('work');

  const handleDelete = (id, type) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (type === 'work') {
        dispatch(deleteWorkExperience(id));
      } else if (type === 'internship') {
        dispatch(deleteInternship(id));
      } else if (type === 'achievement') {
        dispatch(deleteAchievement(id));
      }
    }
  };

  const handleAdd = (type) => {
    setFormType(type);
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item, type) => {
    setFormType(type);
    setEditingItem(item);
    setIsFormOpen(true);
  };

  const handleSave = (itemData) => {
    if (editingItem) {
      // Update existing item
      if (formType === 'work') {
        dispatch(updateWorkExperience(itemData));
      } else if (formType === 'internship') {
        dispatch(updateInternship(itemData));
      } else if (formType === 'achievement') {
        dispatch(updateAchievement(itemData));
      }
    } else {
      // Add new item
      if (formType === 'work') {
        dispatch(addWorkExperience(itemData));
      } else if (formType === 'internship') {
        dispatch(addInternship(itemData));
      } else if (formType === 'achievement') {
        dispatch(addAchievement(itemData));
      }
    }
    setIsFormOpen(false);
    setEditingItem(null);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Experience Management</h2>
      </div>

      {/* Work Experience Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Work Experience</h3>
          <button 
            onClick={() => handleAdd('work')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Work Experience
          </button>
        </div>

        <div className="space-y-4">
          {workExperience.map((exp) => (
            <div key={exp.id} className="bg-surface rounded-lg p-6 security-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-foreground">{exp.role}</h4>
                  <p className="text-primary font-medium">{exp.company}</p>
                  <p className="text-sm text-text-secondary">{exp.period}</p>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleEdit(exp, 'work')}
                    className="p-2 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="Edit" size={16} />
                  </button>
                  <button 
                    onClick={() => handleDelete(exp.id, 'work')}
                    className="p-2 text-text-secondary hover:text-destructive transition-colors"
                  >
                    <Icon name="Trash" size={16} />
                  </button>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-2">
                {exp.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start text-sm">
                    <Icon name="CheckCircle" size={14} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Internships Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Internships & Learning</h3>
          <button 
            onClick={() => handleAdd('internship')}
            className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Internship
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {internshipsAndLearning.map((item) => (
            <div key={item.id} className="bg-surface rounded-lg p-4 security-shadow">
              <div className="flex items-start justify-between mb-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  item.type === 'Current' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
                }`}>
                  {item.type}
                </span>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => handleEdit(item, 'internship')}
                    className="p-1 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="Edit" size={14} />
                  </button>
                  <button 
                    onClick={() => handleDelete(item.id, 'internship')}
                    className="p-1 text-text-secondary hover:text-destructive transition-colors"
                  >
                    <Icon name="Trash" size={14} />
                  </button>
                </div>
              </div>
              
              <h4 className="font-semibold text-foreground mb-2">{item.title}</h4>
              <p className="text-primary font-medium text-sm mb-1">{item.organization}</p>
              <p className="text-xs text-text-secondary mb-2">{item.period}</p>
              <p className="text-text-secondary text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Achievements Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Achievements</h3>
          <button 
            onClick={() => handleAdd('achievement')}
            className="bg-accent text-accent-foreground px-4 py-2 rounded-md hover:bg-accent/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Achievement
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {achievements.map((achievement) => (
            <div key={achievement.id} className="bg-surface rounded-lg p-4 security-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                  <Icon name={achievement.icon} size={20} className="text-accent" />
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => handleEdit(achievement, 'achievement')}
                    className="p-1 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="Edit" size={14} />
                  </button>
                  <button 
                    onClick={() => handleDelete(achievement.id, 'achievement')}
                    className="p-1 text-text-secondary hover:text-destructive transition-colors"
                  >
                    <Icon name="Trash" size={14} />
                  </button>
                </div>
              </div>
              
              <h4 className="font-semibold text-foreground mb-2">{achievement.title}</h4>
              <p className="text-text-secondary text-sm">{achievement.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <ExperienceForm
          item={editingItem}
          type={formType}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default ExperienceAdmin; 